import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import ConfirmDestructive from "../../components/admin/shared/ConfirmDestructive";
import SortableTh from "../../components/admin/shared/SortableTh";
import { useAdminSort } from "../../hooks/useAdminSort";
import {
  fetchAdminLeague, reassignLeagueAdmin,
  adminDeleteLeague, adminRemoveMember, adminAddMember,
  fetchAdminMemberSelections, adminUpdateMemberSelections,
  AdminLeagueDetail, AdminMemberSelections, AdminMemberSelectionsDetail,
} from "../../api/admin";
import { LeagueMode, LeagueStatus } from "../../api/leagues";
import { card, dangerBtn, dangerFilledBtn, inputStyle, smallBtn, secondaryBtn, tableStyle, tdStyle, thStyle, errorStyle, panelHeading } from "../../styles";
import Modal from "../../components/Modal";
import TargetSquadPicker, { TARGET_PLAYER_SLOTS } from "../../components/admin/backfill/TargetSquadPicker";
import FinalPositionsTablePicker from "../../components/admin/backfill/FinalPositionsTablePicker";
import SyndicatePotsPicker, { SyndicatePicksValue } from "../../components/admin/backfill/SyndicatePotsPicker";
import GwChallengePicker, { GwPickerGameweek } from "../../components/admin/backfill/GwChallengePicker";

interface Props {
  user: User;
}

/**
 * One completeness rule shared by the add-member backfill form and the edit-selections
 * modal — both submit through the exact same backend validation, so both need to agree on
 * when a submission is "ready". TARGET/FINAL_POSITIONS always require a full set,
 * regardless of league status (the backend never relaxes this). SYNDICATE only requires
 * completeness once the league has left PREPARING (matching the backend's
 * syndicatePicksAreComplete gate); GAMEWEEK_CHALLENGE and any other mode has no
 * completeness bar in this UI — any subset of picks, including none, is a valid submission.
 */
function selectionsAreComplete(
  mode: LeagueMode,
  status: LeagueStatus,
  state: {
    playerIds: string[];
    predictions: { clubId: string; position: number }[] | null;
    syndicate: SyndicatePicksValue | null;
  },
): boolean {
  if (mode === "TARGET") return state.playerIds.length === TARGET_PLAYER_SLOTS;
  if (mode === "FINAL_POSITIONS") return !!state.predictions && state.predictions.length > 0;
  if (mode === "SYNDICATE") return status === "PREPARING" ? !!state.syndicate : !!state.syndicate?.isComplete;
  return true;
}

export default function LeagueDetailPage({ user }: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<AdminLeagueDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newAdminUserId, setNewAdminUserId] = useState("");
  const [reassigning, setReassigning] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMakeAdmin, setConfirmMakeAdmin] = useState<{ id: string; name: string } | null>(null);
  const [confirmRemoveMember, setConfirmRemoveMember] = useState<{ id: string; name: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [addEmail, setAddEmail] = useState("");
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);
  const [backfillPlayerIds, setBackfillPlayerIds] = useState<string[]>([]);
  const [backfillPredictions, setBackfillPredictions] = useState<{ clubId: string; position: number }[] | null>(null);
  const [backfillSyndicate, setBackfillSyndicate] = useState<SyndicatePicksValue | null>(null);

  const [memberSearch, setMemberSearch] = useState("");
  const { sort: memberSort, toggleSort: toggleMemberSort } = useAdminSort();

  const [editingMember, setEditingMember] = useState<{ id: string; name: string } | null>(null);
  const [editDetail, setEditDetail] = useState<AdminMemberSelectionsDetail | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editPlayerIds, setEditPlayerIds] = useState<string[]>([]);
  const [editPredictions, setEditPredictions] = useState<{ clubId: string; position: number }[] | null>(null);
  const [editSyndicate, setEditSyndicate] = useState<SyndicatePicksValue | null>(null);
  const [editGwcPicks, setEditGwcPicks] = useState<{ gameweekId: string; playerId: string }[]>([]);

  const load = async () => {
    if (!leagueId) return;
    setLoading(true);
    try {
      const data = await fetchAdminLeague(user, leagueId);
      setSelected(data);
      setNewAdminUserId("");
    } catch {
      setError(t("admin.leagues.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [leagueId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await adminDeleteLeague(user, selected.id);
      setConfirmDelete(false);
      navigate("/admin/leagues");
    } catch {
      setError(t("admin.leagues.errors.failedToDelete"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!selected || !newAdminUserId) return;
    setReassigning(true);
    try {
      await reassignLeagueAdmin(user, selected.id, newAdminUserId);
      load();
    } catch {
      setError(t("admin.leagues.errors.failedToSave"));
    } finally {
      setReassigning(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!selected || !confirmRemoveMember) return;
    setActionLoading(true);
    try {
      await adminRemoveMember(user, selected.id, confirmRemoveMember.id);
      setConfirmRemoveMember(null);
      load();
    } catch {
      setError(t("admin.leagues.errors.failedToSave"));
    } finally {
      setActionLoading(false);
    }
  };

  const needsBackfill = selected?.status === "RUNNING"
    && (selected.mode === "TARGET" || selected.mode === "FINAL_POSITIONS" || selected.mode === "SYNDICATE");

  const backfillComplete = !needsBackfill || selectionsAreComplete(selected!.mode, selected!.status, {
    playerIds: backfillPlayerIds, predictions: backfillPredictions, syndicate: backfillSyndicate,
  });

  const handleAddMember = async () => {
    if (!selected || !addEmail.trim()) return;
    setAdding(true);
    setAddError("");
    try {
      let selections: AdminMemberSelections | undefined;
      if (needsBackfill) {
        if (selected.mode === "TARGET") selections = { playerIds: backfillPlayerIds };
        else if (selected.mode === "FINAL_POSITIONS") selections = { predictions: backfillPredictions ?? [] };
        else if (selected.mode === "SYNDICATE") selections = { picks: backfillSyndicate?.picks ?? [], tiebreaker: backfillSyndicate?.tiebreaker ?? 0 };
      }
      await adminAddMember(user, selected.id, addEmail.trim(), selections);
      setAddEmail("");
      setBackfillPlayerIds([]);
      setBackfillPredictions(null);
      setBackfillSyndicate(null);
      load();
    } catch (e: any) {
      setAddError(e?.message ?? t("admin.leagues.errors.failedToSave"));
    } finally {
      setAdding(false);
    }
  };

  const openEditModal = async (memberId: string, name: string) => {
    if (!selected) return;
    setEditingMember({ id: memberId, name });
    setEditDetail(null);
    setEditError("");
    setEditPlayerIds([]);
    setEditPredictions(null);
    setEditSyndicate(null);
    setEditGwcPicks([]);
    setEditLoading(true);
    try {
      const detail = await fetchAdminMemberSelections(user, selected.id, memberId);
      setEditDetail(detail);
      if (detail.mode === "TARGET") setEditPlayerIds(detail.playerIds);
      else if (detail.mode === "FINAL_POSITIONS") setEditPredictions(detail.predictions);
      else if (detail.mode === "GAMEWEEK_CHALLENGE") setEditGwcPicks(detail.picks.map((p) => ({ gameweekId: p.gameweekId, playerId: p.playerId })));
      // SYNDICATE's editSyndicate is seeded by SyndicatePotsPicker itself from
      // editDetail.picks/tiebreaker (via initialPicks/initialTiebreaker), not here.
    } catch {
      setEditError(t("admin.leagues.detail.editSelections.errors.failedToLoad"));
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    setEditingMember(null);
    setEditDetail(null);
    setEditError("");
  };

  const editSaveEnabled = !!editDetail && (
    editDetail.mode === "GAMEWEEK_CHALLENGE"
      ? true
      : selectionsAreComplete(editDetail.mode as LeagueMode, selected?.status ?? "PREPARING", {
          playerIds: editPlayerIds, predictions: editPredictions, syndicate: editSyndicate,
        })
  );

  const handleSaveSelections = async () => {
    if (!selected || !editingMember || !editDetail) return;
    setEditSaving(true);
    setEditError("");
    try {
      let selections: AdminMemberSelections;
      if (editDetail.mode === "TARGET") selections = { playerIds: editPlayerIds };
      else if (editDetail.mode === "FINAL_POSITIONS") selections = { predictions: editPredictions ?? [] };
      else if (editDetail.mode === "SYNDICATE") selections = { picks: editSyndicate?.picks ?? [], tiebreaker: editSyndicate?.tiebreaker ?? 0 };
      else selections = { gwcPicks: editGwcPicks };
      await adminUpdateMemberSelections(user, selected.id, editingMember.id, selections);
      closeEditModal();
      load();
    } catch (e: any) {
      setEditError(e?.message ?? t("admin.leagues.detail.editSelections.errors.failedToSave"));
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) return <p style={{ padding: "1rem", color: "var(--color-text-muted)" }}>{t("common.loading")}</p>;
  if (!selected) return <p style={errorStyle}>{error || t("admin.leagues.errors.failedToLoad")}</p>;

  const approvedMembers = selected.members.filter((m) => m.status === "APPROVED");
  const adminMember = selected.members.find((m) => (m.user?.id ?? "") === selected.adminId);

  // Small, fully-fetched list (one league at a time) — search and sort happen client-side
  // against the already-loaded members, no backend round trip needed.
  const memberName = (m: AdminLeagueDetail["members"][number]) => m.user?.displayName ?? m.guestDisplayName ?? "Guest";
  const searchedMembers = memberSearch.trim()
    ? approvedMembers.filter((m) => {
        const q = memberSearch.trim().toLowerCase();
        return memberName(m).toLowerCase().includes(q) || (m.user?.email ?? "").toLowerCase().includes(q);
      })
    : approvedMembers;
  const visibleMembers = memberSort.sortBy
    ? [...searchedMembers].sort((a, b) => {
        const av = memberSort.sortBy === "email" ? (a.user?.email ?? "") : memberName(a);
        const bv = memberSort.sortBy === "email" ? (b.user?.email ?? "") : memberName(b);
        return av.localeCompare(bv) * (memberSort.sortDir === "asc" ? 1 : -1);
      })
    : searchedMembers;

  return (
    <div>
      {error && <p style={errorStyle}>{error}</p>}

      {/* Page header card */}
      <div style={{ ...card, marginBottom: "1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: "0 0 0.2rem", fontSize: "1.15rem", fontWeight: 700, color: "var(--color-text)" }}>
            {selected.name}
          </h2>
          <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
            {selected.competition.name} · {selected.competition.season}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
          <Link to={`/leagues/${selected.id}`} style={{ ...secondaryBtn, fontSize: "0.85rem", textDecoration: "none" }}>
            {t("admin.leagues.detail.viewLeague")}
          </Link>
          <Link to="/admin/leagues" style={{ ...secondaryBtn, fontSize: "0.85rem", textDecoration: "none" }}>
            ← {t("admin.leagues.backToList")}
          </Link>
        </div>
      </div>

      {/* Reassign admin — card with header */}
      <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1rem" }}>
        <div style={{ padding: "1.1rem 1.25rem 0.4rem" }}>
          <h3 style={{ ...panelHeading, margin: 0 }}>
            League Admin
          </h3>
        </div>
        <div style={{ padding: "1rem 1.25rem" }}>
          <p style={{ fontSize: "0.9rem", margin: "0 0 0.75rem", color: "var(--color-text)" }}>
            Current admin: <strong>{adminMember?.user?.displayName ?? "—"}</strong>
          </p>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <select
              aria-label="Reassign league admin to"
              style={{ flex: 1, padding: "0.5rem 0.75rem", borderRadius: "10px", border: "1px solid var(--color-input-border)", background: "var(--color-input-bg)", color: "var(--color-input-text)", fontSize: "0.9rem" }}
              value={newAdminUserId}
              onChange={(e) => setNewAdminUserId(e.target.value)}
            >
              <option value="">Reassign to...</option>
              {approvedMembers
                .filter((m) => m.user && m.user.id !== selected.adminId)
                .map((m) => (
                  <option key={m.id} value={m.user!.id}>
                    {m.user!.displayName}
                  </option>
                ))}
            </select>
            <button
              style={smallBtn}
              disabled={!newAdminUserId || reassigning}
              onClick={() => {
                const candidate = approvedMembers.find((m) => m.user?.id === newAdminUserId);
                setConfirmMakeAdmin({ id: newAdminUserId, name: candidate?.user?.displayName ?? "" });
              }}
            >
              {reassigning ? t("common.saving") : t("admin.leagues.detail.makeAdmin")}
            </button>
          </div>
        </div>
      </div>

      {/* Members table — card with header */}
      <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1rem" }}>
        <div style={{ padding: "1.1rem 1.25rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <h3 style={{ ...panelHeading, margin: 0 }}>
            {t("admin.leagues.detail.members")}
          </h3>
          {approvedMembers.length > 0 && (
            <input
              style={{ ...inputStyle, width: "200px" }}
              placeholder={t("admin.leagues.detail.searchMembers")}
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
            />
          )}
        </div>
        {approvedMembers.length === 0 ? (
          <p style={{ padding: "0 1.25rem 1.1rem", color: "var(--color-text-muted)" }}>{t("admin.leagues.detail.noMembers")}</p>
        ) : visibleMembers.length === 0 ? (
          <p style={{ padding: "0 1.25rem 1.1rem", color: "var(--color-text-muted)" }}>{t("admin.leagues.detail.noMemberSearchResults")}</p>
        ) : (
          <div style={isMobile ? { overflowX: "auto" } : undefined}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <SortableTh label={t("admin.leagues.detail.columns.member")} sortKey="member" activeSortBy={memberSort.sortBy} sortDir={memberSort.sortDir} onSort={toggleMemberSort} />
                  <SortableTh label={t("admin.leagues.detail.columns.email")} sortKey="email" activeSortBy={memberSort.sortBy} sortDir={memberSort.sortDir} onSort={toggleMemberSort} />
                  <th style={thStyle}>{t("admin.leagues.detail.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleMembers.map((m) => {
                  // Best-effort UI hint, not the real guard — the backend 403s a site
                  // admin editing their own selections regardless of what this hides.
                  const isSelf = !!m.user?.email && m.user.email === user.email;
                  const canEditSelections = selected.mode !== "SCORE_PREDICTOR" && !isSelf;
                  return (
                    <tr key={m.id}>
                      <td style={tdStyle}>{memberName(m)}</td>
                      <td style={tdStyle}>{m.user?.email ?? "—"}</td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: "0.4rem" }}>
                          {canEditSelections && (
                            <button style={smallBtn} onClick={() => openEditModal(m.id, memberName(m))}>
                              {t("admin.leagues.detail.editSelections.action")}
                            </button>
                          )}
                          <button style={dangerBtn} onClick={() => setConfirmRemoveMember({ id: m.id, name: memberName(m) })}>
                            {t("admin.leagues.detail.removeMember")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add member */}
      <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1rem" }}>
        <div style={{ padding: "1.1rem 1.25rem 0.4rem" }}>
          <h3 style={{ ...panelHeading, margin: 0 }}>
            {t("admin.leagues.detail.addMember")}
          </h3>
        </div>
        {selected.status === "COMPLETED" ? (
          <div style={{ padding: "1rem 1.25rem" }}>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              {t("admin.leagues.detail.backfill.completedNotice")}
            </p>
          </div>
        ) : (
          <div style={{ padding: "1rem 1.25rem" }}>
            {addError && <p style={{ ...errorStyle, marginBottom: "0.5rem" }}>{addError}</p>}
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: needsBackfill ? "0.9rem" : 0 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                type="email"
                placeholder={t("admin.leagues.detail.addMemberPlaceholder")}
                value={addEmail}
                onChange={(e) => { setAddEmail(e.target.value); setAddError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter" && backfillComplete) handleAddMember(); }}
              />
              <button style={smallBtn} onClick={handleAddMember} disabled={!addEmail.trim() || adding || !backfillComplete}>
                {adding ? t("common.saving") : t("admin.leagues.detail.addMemberBtn")}
              </button>
            </div>

            {needsBackfill && (
              <div>
                <p style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", marginTop: 0 }}>
                  {t("admin.leagues.detail.backfill.runningNotice")}
                </p>
                {selected.mode === "TARGET" && (
                  <>
                    <h4 style={{ fontSize: "0.85rem", margin: "0 0 0.4rem" }}>{t("admin.leagues.detail.backfill.target.heading")}</h4>
                    <TargetSquadPicker
                      user={user}
                      competitionId={selected.competitionId}
                      selectedIds={backfillPlayerIds}
                      onChange={setBackfillPlayerIds}
                    />
                  </>
                )}
                {selected.mode === "FINAL_POSITIONS" && (
                  <>
                    <h4 style={{ fontSize: "0.85rem", margin: "0 0 0.4rem" }}>{t("admin.leagues.detail.backfill.finalPositions.heading")}</h4>
                    <FinalPositionsTablePicker
                      user={user}
                      competitionId={selected.competitionId}
                      onChange={setBackfillPredictions}
                    />
                  </>
                )}
                {selected.mode === "SYNDICATE" && (
                  <>
                    <h4 style={{ fontSize: "0.85rem", margin: "0 0 0.4rem" }}>{t("admin.leagues.detail.backfill.syndicate.heading")}</h4>
                    <SyndicatePotsPicker
                      user={user}
                      competitionId={selected.competitionId}
                      onChange={setBackfillSyndicate}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Danger zone */}
      <div style={{ marginTop: "0.5rem" }}>
        <button style={dangerBtn} onClick={() => setConfirmDelete(true)}>
          {t("admin.leagues.detail.delete")}
        </button>
      </div>

      {confirmDelete && (
        <ConfirmDestructive
          title={t("admin.leagues.detail.confirmDelete.title")}
          message={t("admin.leagues.detail.confirmDelete.message", { name: selected.name })}
          confirmLabel={t("common.delete")}
          confirming={actionLoading}
          irreversible
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {confirmRemoveMember && (
        <ConfirmDestructive
          title={t("admin.leagues.detail.confirmRemoveMember.title")}
          message={t("admin.leagues.detail.confirmRemoveMember.message", { name: confirmRemoveMember.name })}
          confirmLabel={t("admin.leagues.detail.removeMember")}
          confirming={actionLoading}
          onConfirm={handleRemoveMember}
          onCancel={() => setConfirmRemoveMember(null)}
        />
      )}

      {confirmMakeAdmin && (
        <ConfirmDestructive
          title={t("admin.leagues.detail.confirmMakeAdmin.title")}
          message={t("admin.leagues.detail.confirmMakeAdmin.message", { name: confirmMakeAdmin.name })}
          confirmLabel={t("admin.leagues.detail.makeAdmin")}
          confirming={reassigning}
          onConfirm={async () => { await handleReassign(); setConfirmMakeAdmin(null); }}
          onCancel={() => setConfirmMakeAdmin(null)}
        />
      )}

      {editingMember && (
        <Modal onClose={closeEditModal} blur={false}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--color-surface)", borderRadius: "20px", border: "1px solid var(--color-border)",
              padding: "1.5rem", maxWidth: "560px", width: "90%", maxHeight: "85vh", overflowY: "auto",
            }}
          >
            <h3 style={{ marginTop: 0 }}>{t("admin.leagues.detail.editSelections.modalTitle", { name: editingMember.name })}</h3>
            <p style={{ fontSize: "0.85rem", color: "var(--color-danger)", background: "var(--color-danger-glow, rgba(220,38,38,0.08))", border: "1px solid var(--color-danger)", borderRadius: "10px", padding: "0.6rem 0.9rem" }}>
              {t("admin.leagues.detail.editSelections.warning", { name: editingMember.name })}
            </p>
            {editError && <p style={errorStyle}>{editError}</p>}
            {editLoading || !editDetail ? (
              <p style={{ color: "var(--color-text-muted)" }}>{t("common.loading")}</p>
            ) : (
              <>
                {editDetail.mode === "TARGET" && (
                  <>
                    <h4 style={{ fontSize: "0.85rem", margin: "0 0 0.4rem" }}>{t("admin.leagues.detail.backfill.target.heading")}</h4>
                    <TargetSquadPicker
                      user={user}
                      competitionId={selected.competitionId}
                      selectedIds={editPlayerIds}
                      onChange={setEditPlayerIds}
                      initialPlayers={editDetail.players}
                    />
                  </>
                )}
                {editDetail.mode === "FINAL_POSITIONS" && (
                  <>
                    <h4 style={{ fontSize: "0.85rem", margin: "0 0 0.4rem" }}>{t("admin.leagues.detail.backfill.finalPositions.heading")}</h4>
                    <FinalPositionsTablePicker
                      user={user}
                      competitionId={selected.competitionId}
                      onChange={setEditPredictions}
                      initialPredictions={editDetail.predictions}
                    />
                  </>
                )}
                {editDetail.mode === "SYNDICATE" && (
                  <>
                    <h4 style={{ fontSize: "0.85rem", margin: "0 0 0.4rem" }}>{t("admin.leagues.detail.backfill.syndicate.heading")}</h4>
                    <SyndicatePotsPicker
                      user={user}
                      competitionId={selected.competitionId}
                      onChange={setEditSyndicate}
                      initialPicks={editDetail.picks}
                      initialTiebreaker={editDetail.tiebreaker}
                    />
                  </>
                )}
                {editDetail.mode === "GAMEWEEK_CHALLENGE" && (
                  <>
                    <h4 style={{ fontSize: "0.85rem", margin: "0 0 0.4rem" }}>{t("admin.leagues.detail.editSelections.gwc.heading")}</h4>
                    <GwChallengePicker
                      user={user}
                      competitionId={selected.competitionId}
                      gameweeks={editDetail.gameweeks as GwPickerGameweek[]}
                      initialPicks={editDetail.picks}
                      onChange={setEditGwcPicks}
                    />
                  </>
                )}
              </>
            )}
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "1.25rem" }}>
              <button style={secondaryBtn} onClick={closeEditModal}>
                {t("admin.leagues.detail.editSelections.cancel")}
              </button>
              <button
                style={dangerFilledBtn}
                onClick={handleSaveSelections}
                disabled={editSaving || editLoading || !editSaveEnabled}
              >
                {editSaving ? t("admin.leagues.detail.editSelections.saving") : t("admin.leagues.detail.editSelections.save")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
